// Keyboard, autofill and IME hints for every text input in the app.
//
// Typing a card on a phone is the app's main job, so each field should summon
// the right keyboard (a keypad for a phone number, an @ key for an email),
// offer the browser's saved value, capitalise the way that field is normally
// written, and label the Enter key with what it will actually do. Getting this
// wrong is invisible on a desktop and painful on a phone.
//
// The hints live here rather than being sprinkled across templates so a field
// cannot be added with half of them.
import { noChange } from 'lit';
import { directive, Directive, PartType, type ElementPart, type PartInfo } from 'lit/directive.js';

/** The kinds of field the app actually has. */
export type FieldKind =
  | 'name' | 'title' | 'company' | 'monogram'
  | 'phone' | 'email' | 'website' | 'address' | 'tagline'
  | 'search' | 'prompt' | 'sendTo' | 'otp' | 'text';

interface Hints {
  type?: string;
  inputmode?: string;
  autocomplete?: string;
  autocapitalize?: string;
  spellcheck?: string;
  /** Safari's pre-standard sibling of spellcheck; harmless elsewhere. */
  autocorrect?: string;
  enterkeyhint?: string;
}

// Defaults for a plain text field. Everything below overrides only what differs.
const BASE: Hints = {
  type: 'text',
  autocomplete: 'off',
  autocapitalize: 'sentences',
  spellcheck: 'false',
  autocorrect: 'off',
  enterkeyhint: 'next',
};

const HINTS: Record<FieldKind, Hints> = {
  text: {},

  // --- identity: autofill from the browser's saved contact, Title Case ---
  name: { autocomplete: 'name', autocapitalize: 'words' },
  title: { autocomplete: 'organization-title', autocapitalize: 'words' },
  company: { autocomplete: 'organization', autocapitalize: 'words' },
  // Initials — always upper case, never a saved value, never spell-checked.
  monogram: { autocapitalize: 'characters' },

  // --- contact: each summons its own keyboard ---
  phone: { type: 'tel', inputmode: 'tel', autocomplete: 'tel' },
  email: { type: 'email', inputmode: 'email', autocomplete: 'email', autocapitalize: 'none' },
  website: { type: 'url', inputmode: 'url', autocomplete: 'url', autocapitalize: 'none' },
  address: { autocomplete: 'street-address', autocapitalize: 'words' },

  // --- prose: the one place spell-check earns its keep ---
  tagline: { spellcheck: 'true', autocorrect: 'on', enterkeyhint: 'done' },
  prompt: { spellcheck: 'true', autocorrect: 'on', enterkeyhint: 'go' },

  // A one-time code: digits only, so summon a numeric keypad. one-time-code
  // lets iOS and Android offer the code straight from the notification, which
  // is the difference between one tap and switching apps to read it.
  otp: { inputmode: 'numeric', autocomplete: 'one-time-code', autocapitalize: 'none', enterkeyhint: 'go' },

  // --- utility ---
  search: { type: 'search', autocapitalize: 'none', enterkeyhint: 'search' },
  // Accepts a phone, an email or a handle. inputmode:email keeps letters, @ and
  // the dot one tap away, which covers two of the three without hurting the
  // third; the Enter key promises "send", because that is what it does.
  sendTo: { inputmode: 'email', autocapitalize: 'none', enterkeyhint: 'send' },
};

/** Options a call site can override per input. */
export interface HintOptions {
  /** Usually 'next' mid-form and 'done' on the last field. */
  enterKeyHint?: string;
}

/** Resolve the attribute set for a field. Exported for tests. */
export function hintsFor(kind: FieldKind, opts?: HintOptions): Record<string, string> {
  const merged: Hints = { ...BASE, ...HINTS[kind] };
  if (opts?.enterKeyHint) merged.enterkeyhint = opts.enterKeyHint;
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(merged)) {
    if (value != null) out[key] = value;
  }
  return out;
}

class InputHintsDirective extends Directive {
  constructor(partInfo: PartInfo) {
    super(partInfo);
    if (partInfo.type !== PartType.ELEMENT) {
      throw new Error('inputHints() must be used as an element binding: <input ${inputHints(…)}>');
    }
  }

  // An element binding renders nothing — all the work happens in update(),
  // which has the part (and so the element) to set attributes on. The params
  // are still declared because directive() derives the call signature from
  // render(); the leading underscore marks them intentionally unused.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  render(_kind: FieldKind, _opts?: HintOptions) {
    return noChange;
  }

  update(part: ElementPart, [kind, opts]: [FieldKind, HintOptions?]) {
    const el = part.element;
    const isTextarea = el.tagName === 'TEXTAREA';
    for (const [attr, value] of Object.entries(hintsFor(kind, opts))) {
      // <textarea> has no `type`; setting one would be invalid markup.
      if (isTextarea && attr === 'type') continue;
      el.setAttribute(attr, value);
    }
    return noChange;
  }
}

/**
 * Apply a field's keyboard/autofill hints as one element binding:
 *
 *     <input ${inputHints('email')} .value=${…} />
 */
export const inputHints = directive(InputHintsDirective);
